<template>
  <div>
    <div v-if="assetsAndUploadingAssets.length > 0" class="list-actions">
      <div class="select">
        <ts-link @click="handleSelectAllAssetsClick">Select all</ts-link>
        <ts-link @click="handleUnselectAllAssetsClick">Unselect all</ts-link>
      </div>
      <div class="actions" v-show="hasSelectedAssets">
        <share-working-files :assets="selectedUploadedAssets" v-if="isAdmin || !isCreator"></share-working-files>
        <ts-button size="small" v-show="canUserDeleteAssets" @click="handleDeleteAssetsClick(selectedAssets)">
          Delete
        </ts-button>
        <ts-button :pending="!canDownloadAssets" size="small" @click="handleDownloadAssetsClick(selectedAssets)">
          Download
        </ts-button>
      </div>
    </div>
    <div ref="droptarget" class="droptarget" @dragover="isDragging = true" @dragleave="isDragging = false">
      <div class="list">
        <div
          v-for="asset in assetsAndUploadingAssets"
          :key="asset.id"
          class="row"
          :class="{ 'upload-error': asset.uploadError }"
        >
          <ts-checkbox
            class="checkbox"
            :value="isAssetSelected(asset)"
            @input="handleAssetSelectedChange(asset, $event)"
          ></ts-checkbox>
          <div class="thumbnail">
            <Thumbnail v-if="asset.file && asset.file.thumbnail" :url="asset.file.thumbnail" />
          </div>
          <div class="file-info" :class="{ uploading: isAssetUploading(asset) }">
            <p class="file-name">{{ asset.name }}{{ asset.error }}</p>
            <div v-if="asset.uploadError" class="upload-error-message">
              {{ asset.uploadErrorMessage }}
            </div>
            <div v-else-if="isAssetUploading(asset)">
              <ts-progress :percentage="asset.resumableFile.tsUploadProgress * 100"></ts-progress>
            </div>
            <p v-else class="file-creation-info">
              Created on {{ formatDate(asset.createdAt) }} by {{ asset.createdBy.name }}
            </p>
          </div>
          <visible :when="isProjectManager && !isAssetUploading(asset)" class="users">
            <UserPicker :options="users" :value="asset.users" @input="handleAssetUsersChange(asset, $event)" />
          </visible>
          <div class="actions">
            <div v-if="!isAssetUploading(asset)" class="actions">
              <ts-button
                :pending="isPending(asset.status)"
                v-if="asset.type === 'uploaded-file'"
                size="small"
                circle
                @click="handleDownloadAssetClick(asset)"
              >
                <ts-icon
                  icon="save"
                  size="x-small"
                  color="#6d7587"
                  :title="isPending(asset.status) ? 'Pending' : 'Download'"
                  :pending="isPending(asset.status)"
                />
              </ts-button>
              <FilePreview
                v-if="asset.type === 'uploaded-file' && isFilePreviewable(asset.file)"
                :file="asset.file"
                :show-edit="false"
                @delete="handleDeleteAssetsClick([asset])"
              />
              <ts-button v-if="asset.type === 'google-drive-link'" size="small" circle @click="handleLinkCopy(asset)">
                <ts-icon icon="link" size="x-small" color="#6d7587" title="Copy" />
              </ts-button>
              <EditWorkingFile v-if="canUserEdit(asset)" :value="asset" @input="handleAssetEdit" />
            </div>
            <ts-button v-if="canUserEdit(asset)" size="small" circle @click="handleDeleteAssetsClick([asset])">
              <ts-icon icon="trash" size="x-small" color="#6d7587" title="Delete" />
            </ts-button>
          </div>
        </div>
      </div>
      <div v-if="isProjectManager || isCreator" ref="droptargetPlaceholder" class="placeholder">
        <DropTarget label="Working files" :active="isDragging" :show-dropin="assetsAndUploadingAssets.length === 0" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.list-actions {
  margin-bottom: 20px;
  display: flex;

  .select {
    flex-grow: 1;

    > * {
      margin-right: 23px;
    }
  }

  .actions {
    display: flex;

    > * {
      margin-left: 8px;
    }
  }
}

.droptarget {
  .placeholder {
    display: flex;
    justify-content: center;
    margin: 50px;
  }
}

.row {
  background: #ebecf0;
  background-image: linear-gradient(-42deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0.4) 100%);
  background-blend-mode: soft-light, normal;
  background-clip: padding-box;
  border: 2px solid rgba(255, 255, 255, 0.16);
  box-shadow: -2px -2px 6px 0 rgba(250, 251, 255, 0.48), 2px 2px 6px 0 rgba(166, 171, 189, 0.24);
  border-radius: 16px;
  margin-bottom: 10px;

  display: flex;
  align-items: center;
  padding: 12px 18px;

  &.upload-error {
    border-color: #f56c6c;
  }

  .checkbox {
    margin-right: 13px;
  }

  .thumbnail {
    width: 48px;
    margin-right: 12px;
  }

  .file-info {
    width: 250px;
    padding-right: 12px;

    &.uploading {
      width: auto;
      flex-grow: 1;
    }
  }

  .file-name {
    font-family: Roboto;
    font-size: 12px;
    color: #6d7587;
    overflow-wrap: break-word;
  }

  .file-creation-info {
    font-family: Roboto;
    font-size: 10px;
    line-height: 16px;
    color: #acbacc;
  }

  .upload-error-message {
    font-family: Roboto;
    font-size: 10px;
    line-height: 16px;
    color: #f56c6c;
  }

  .users {
    flex-grow: 1;
  }

  .actions {
    display: flex;
    align-items: center;

    > * {
      margin-left: 10px;
    }
  }
}
</style>

<script>
import { mapState, mapActions } from 'vuex';
import { sortBy } from 'lodash';

import { downloadProjectWorkingFiles } from '@/api/workflow';

import EditWorkingFile from './EditWorkingFile.vue';
import Thumbnail from '@/components/Thumbnail.vue';
import DropTarget from '@/components/DropTarget.vue';
import UserPicker from '@/components/inputs/UserPicker.vue';
import FilePreview from '@/components/FilePreview.vue';
import ShareWorkingFiles from './ShareWorkingFiles.vue';

import projectViewHelpers from '@/mixins/projectViewHelpers';
import uploader from '@/mixins/uploader';

export default {
  mixins: [projectViewHelpers, uploader],
  components: {
    EditWorkingFile,
    Thumbnail,
    DropTarget,
    ShareWorkingFiles,
    UserPicker,
    FilePreview
  },
  props: {
    assets: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedAssets: [],
      isDragging: false,
      uploadingAssets: []
    };
  },
  computed: {
    ...mapState('workflow', {
      platforms: state => state.platforms.items,
      users: state => state.currentProject.value?.users || [],
      currentProjectId: state => state.currentProjectId
    }),
    hasSelectedAssets() {
      return this.selectedAssets.length > 0;
    },
    canDownloadAssets() {
      return (
        this.selectedAssets.length > 0 &&
        this.selectedAssets.every(asset => {
          return asset.file?.status === 'processed';
        })
      );
    },
    canUserDeleteAssets() {
      return this.selectedAssets.every(asset => this.canUserEdit(asset));
    },
    filteredAssets() {
      return this.assets.filter(asset => this.canUserSee(asset));
    },
    assetsAndUploadingAssets() {
      return [...this.filteredAssets, ...this.uploadingAssets];
    },
    selectedUploadedAssets() {
      return this.selectedAssets.filter(asset => !asset.resumableFile);
    }
  },
  methods: {
    ...mapActions('workflow/delivery', [
      'addCurrentProjectWorkingFile',
      'updateCurrentProjectWorkingFile',
      'updateCurrentProjectWorkingFileUsers',
      'removeCurrentProjectWorkingFile'
    ]),
    canUserEdit(asset) {
      if (this.isProjectManager) return true;

      if (asset.createdBy && asset.createdBy.id == this.user.id) {
        return true;
      }

      return false;
    },
    canUserSee(asset) {
      return this.canUserEdit(asset) || asset.users.map(u => u.id).includes(this.user.uuid);
    },
    isAssetSelected(asset) {
      return !!this.selectedAssets.find(a => a.id === asset.id);
    },
    isAssetUploading(asset) {
      return asset.resumableFile && !asset.resumableFile.tsIsComplete;
    },
    handleSelectAllAssetsClick() {
      this.selectedAssets = this.filteredAssets;
    },
    handleUnselectAllAssetsClick() {
      this.selectedAssets = [];
    },
    handleAssetSelectedChange(asset, value) {
      if (value) {
        this.selectedAssets = [...this.selectedAssets, asset];
      } else {
        this.selectedAssets = this.selectedAssets.filter(a => a.id !== asset.id);
      }
    },
    handleDownloadAssetClick(asset) {
      if (this.isPending(asset.status)) {
        window.EventBus.$emit('download.pending');
        return;
      }
      window.open(asset.file.url, '_blank');
    },
    handleAssetUsersChange(asset, value) {
      this.updateCurrentProjectWorkingFileUsers({
        asset: {
          ...asset,
          users: value
        }
      });
    },
    handleAssetEdit(asset) {
      this.updateCurrentProjectWorkingFile({ asset });
    },
    handleDeleteAssetsClick(assets) {
      assets
        .filter(asset => !!asset.resumableFile)
        .forEach(asset => {
          asset.resumableFile.resumableObj.removeFile(asset.resumableFile);
          this.uploadingAssets = this.uploadingAssets.filter(
            a => a.resumableFile.uniqueIdentifier !== asset.resumableFile.uniqueIdentifier
          );
          this.$emit('delete-asset', asset);
        });
      assets
        .filter(asset => !asset.resumableFile)
        .forEach(asset => {
          this.removeCurrentProjectWorkingFile({ asset });
        });
    },
    async handleLinkCopy(asset) {
      try {
        await this.$copyText(asset.url);
        this.showInfo('Link copied to clipboard');
      } catch (err) {
        console.error('Error when copying step link', err);
        this.showError('Error when copying step link');
      }
    },
    handleDownloadAssetsClick(assets) {
      if (!this.canDownloadAssets) {
        window.EventBus.$emit('download.pending');
        return;
      }

      const uploadedAssets = assets.filter(asset => !asset.resumableFile);

      if (uploadedAssets.length > 1) {
        this.showDownloadProcessing();
        this.downloadFile(
          downloadProjectWorkingFiles({
            projectId: this.currentProjectId,
            assetIds: uploadedAssets.map(a => a.id)
          }),
          `ts_working_files_project${this.currentProjectId}.zip`
        );
        return;
      }

      uploadedAssets.length === 1 && this.handleDownloadAssetClick(uploadedAssets[0]);
    }
  },
  mounted() {
    if (this.isProjectManager || this.isCreator) {
      this.resumable.assignBrowse(this.$refs.droptargetPlaceholder);
      this.resumable.assignDrop(this.$refs.droptarget);
    }
    this.resumable.on('fileAdded', file => {
      this.uploadingAssets = [
        ...this.uploadingAssets,
        {
          id: file.uniqueIdentifier,
          name: file.fileName,
          type: 'uploaded-file',
          resumableFile: file,
          users: [],
          createdAt: new Date().toISOString(),
          createdBy: this.currentUser,
          uploadError: false,
          uploadErrorMessage: ''
        }
      ];
      this.resumable.upload();
    });
    this.resumable.on('fileSuccess', async file => {
      const asset = this.uploadingAssets.find(a => a.resumableFile.uniqueIdentifier == file.uniqueIdentifier);
      if (asset) {
        try {
          await this.addCurrentProjectWorkingFile({ asset });
          this.uploadingAssets = this.uploadingAssets.filter(
            a => a.resumableFile.uniqueIdentifier !== file.uniqueIdentifier
          );
        } catch (err) {
          console.error('Server error when adding working file', err);
          asset.uploadError = true;
          asset.uploadErrorMessage = err.message;
          this.showError('Error when adding working file');
        }
      }
    });
    this.resumable.on('fileError', async (file, message) => {
      const asset = this.uploadingAssets.find(a => a.resumableFile.uniqueIdentifier == file.uniqueIdentifier);
      if (asset) {
        asset.uploadError = true;
        asset.uploadErrorMessage = message;
      }
    });
  }
};
</script>
